import { Globe, MapPin, Mail, ArrowUpRight, AlertCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";

const options = [
  {
    icon: Globe,
    title: "Online Portal",
    body: "The fastest way to pay. Log in to the customer portal any time to make a one-time payment, set up recurring payments, or review your payment history. Please note: additional fees apply to payments made through the online portal.",
    detail: "Available 24/7 · Have your account number ready · Additional fees apply",
    cta: "Go to the payment portal",
    href: "https://my.blytzpay.com/#/merchant/7acdb786-1f15-4eca-aff5-f4b1fea02516/login",
    testid: "payment-option-online",
  },
  {
    icon: MapPin,
    title: "In Person",
    body: "Prefer to pay face to face? Visit us at 908 S. Buckner Blvd, Dallas, TX 75217 and a member of our team will take your payment and answer any account questions on the spot.",
    detail: "908 S. Buckner Blvd, Dallas, TX 75217 · Mon–Sat, 9:00 AM – 6:00 PM CT",
    cta: "Get directions",
    href: "https://www.google.com/maps/search/?api=1&query=908+S+Buckner+Blvd%2C+Dallas%2C+TX+75217",
    testid: "payment-option-in-person",
  },
  {
    icon: Mail,
    title: "By Mail",
    body: "Send a check or money order with your account number written clearly on the memo line. Allow 5–7 business days for delivery and posting.",
    detail: "Concorde Auto Acceptance · 908 S. Buckner Blvd · Dallas, TX 75217",
    cta: null,
    testid: "payment-option-mail",
  },
];

const Payment = () => (
  <>
    <PageHeader
      testid="payment-header"
      overline="Make a Payment"
      title="Three simple ways"
      accent="to pay."
      description={
        <>
          <span className="block font-bold text-slate-900 uppercase" data-testid="payment-notice">
            PLEASE NOTE THAT ALL PAYMENTS MUST BE MADE ON TIME, NO EXCEPTIONS.
            THERE IS NO GRACE PERIOD.
          </span>
          <span className="block mt-4">
            Concorde Auto Acceptance reports to Experian and Equifax monthly to
            show other creditors your new credit history. Many of our customers
            have regained the convenience of using credit cards, or even
            purchased new homes by making timely payments.
          </span>
          <span className="block mt-4">
            For your convenience, we have a number of convenient options for
            making your payments on time. Choose the option that best works for
            you.
          </span>
        </>
      }
    />
    <section className="pb-24 lg:pb-32" data-testid="payment-options">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {options.map((o, i) => (
          <Reveal key={o.title} delay={i * 0.08}>
            <div
              data-testid={o.testid}
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-12 lg:py-16 border-t border-slate-900/10 items-start"
            >
              <div className="lg:col-span-9">
                <h2 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight flex items-center gap-3">
                  <o.icon size={24} className="text-ember" />
                  {o.title}
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">{o.body}</p>
                <p className="mt-4 font-mono text-xs tracking-wide text-slate-600">{o.detail}</p>
              </div>
              <div className="lg:col-span-3 flex lg:justify-end">
                {o.cta && (
                  <a
                    href={o.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${o.testid}-cta`}
                    className="inline-flex items-center gap-2 rounded-full border border-ember/50 px-6 py-3 text-sm font-bold text-ember transition-all duration-300 hover:bg-ember hover:text-white"
                  >
                    {o.cta} <ArrowUpRight size={15} />
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.1}>
          <div
            data-testid="payment-note"
            className="mt-8 rounded-2xl border border-slate-900/10 bg-white/75 backdrop-blur-xl p-8 flex items-start gap-4"
          >
            <AlertCircle size={22} className="text-ember shrink-0 mt-0.5" />
            <div>
              <Overline>Before you pay</Overline>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl">
                Have your Concorde Auto Acceptance account number ready — it
                appears on your welcome letter and every statement. If you are
                experiencing financial hardship, please call us before your due
                date; our team can walk you through available assistance options.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Payment;
